import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	JOB_GROUP_ORDER,
	buildJobGroupRowSpans,
	getAttendanceJobGroup,
} from "./attendanceHelpers";
import LeavePromotionPanels from "./LeavePromotionPanels";
import {
	Button,
	Card,
	Form,
	Input,
	InputNumber,
	Modal,
	Radio,
	Space,
	Table,
	Typography,
	message,
} from "antd";
import PageHeader from "../common/PageHeader";

const { Text } = Typography;
const { TextArea } = Input;

const promotionTargets = (rows) =>
	(rows || []).filter((row) => row.is_home_church !== false);

const LeaveBalanceManage = () => {
	const [loading, setLoading] = useState(false);
	const [asOf, setAsOf] = useState("");
	const [employees, setEmployees] = useState([]);
	const [resetSoonSix, setResetSoonSix] = useState([]);
	const [resetSoonTwo, setResetSoonTwo] = useState([]);
	const [firstYearNine, setFirstYearNine] = useState([]);
	const [firstYearTwo, setFirstYearTwo] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [sortMode, setSortMode] = useState("name");
	const [carryoverTarget, setCarryoverTarget] = useState(null);
	const [priorTarget, setPriorTarget] = useState(null);
	const [saving, setSaving] = useState(false);
	const [carryoverForm] = Form.useForm();
	const [priorForm] = Form.useForm();

	const loadBalances = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/leave/annual-balance/",
			);
			setAsOf(response.data.as_of);
			setEmployees(response.data.employees || []);
			setResetSoonSix(
				promotionTargets(
					response.data.reset_soon_six_months ||
						response.data.reset_soon ||
						[],
				),
			);
			setResetSoonTwo(
				promotionTargets(
					response.data.reset_soon_two_months || [],
				),
			);
			setFirstYearNine(
				promotionTargets(
					response.data.reset_soon_first_year_nine || [],
				),
			);
			setFirstYearTwo(
				promotionTargets(
					response.data.reset_soon_first_year_two || [],
				),
			);
		} catch (error) {
			message.error("잔여 연차를 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadBalances();
	}, []);

	const filtered = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		let list = employees;
		if (keyword) {
			list = list.filter((row) =>
				(row.name || "")
					.toLowerCase()
					.includes(keyword),
			);
		}
		const sorted = [...list];
		if (sortMode === "job") {
			sorted.sort((a, b) => {
				const ga = getAttendanceJobGroup(a);
				const gb = getAttendanceJobGroup(b);
				const ia = JOB_GROUP_ORDER.indexOf(ga);
				const ib = JOB_GROUP_ORDER.indexOf(gb);
				if (ia !== ib) return ia - ib;
				return String(a.name || "").localeCompare(
					String(b.name || ""),
					"ko",
				);
			});
		} else {
			sorted.sort((a, b) =>
				String(a.name || "").localeCompare(
					String(b.name || ""),
					"ko",
				),
			);
		}
		return sorted;
	}, [employees, searchText, sortMode]);

	const groupSpans = useMemo(
		() =>
			sortMode === "job"
				? buildJobGroupRowSpans(filtered)
				: [],
		[filtered, sortMode],
	);

	const openCarryover = (row) => {
		setCarryoverTarget(row);
		carryoverForm.setFieldsValue({
			days: row.carry_forward_days || 0,
			note: row.note || "",
		});
	};

	const openPrior = (row) => {
		setPriorTarget(row);
		priorForm.setFieldsValue({
			prior_annual_days: row.prior_annual_days || 0,
			prior_monthly_days: row.prior_monthly_days || 0,
			note: row.note || "",
		});
	};

	const saveCarryover = async () => {
		const values = await carryoverForm.validateFields();
		try {
			setSaving(true);
			await useAxios.post(
				`/utils/leave/annual-balance/${carryoverTarget.user_id}/carryover/`,
				{
					days: values.days,
					note: values.note || "",
				},
			);
			message.success(
				"다음 기간으로 이월할 일수를 저장했습니다.",
			);
			setCarryoverTarget(null);
			await loadBalances();
		} catch (error) {
			message.error(
				error?.response?.data?.error ||
					"이월 처리에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const savePrior = async () => {
		const values = await priorForm.validateFields();
		try {
			setSaving(true);
			await useAxios.post(
				`/utils/leave/annual-balance/${priorTarget.user_id}/prior-usage/`,
				values,
			);
			message.success("이전 사용 연차·월차를 반영했습니다.");
			setPriorTarget(null);
			await loadBalances();
		} catch (error) {
			message.error(
				error?.response?.data?.error ||
					"이전 사용분 저장에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const baseColumns = [
		{
			title: "이름",
			dataIndex: "name",
			width: 110,
			fixed: "left",
		},
		{ title: "직분", dataIndex: "job", width: 90 },
		{ title: "입사일", dataIndex: "hire_date", width: 120 },
		{
			title: "발생",
			dataIndex: "granted_days",
			width: 80,
			align: "right",
		},
		{
			title: "받은 이월",
			dataIndex: "carried_in_days",
			width: 90,
			align: "right",
		},
		{
			title: "다음 기간 이월",
			dataIndex: "carry_forward_days",
			width: 120,
			align: "right",
		},
		{
			title: "이전 사용",
			dataIndex: "prior_deduction_days",
			width: 170,
			align: "right",
			render: (value, row) => (
				<span style={{ whiteSpace: "nowrap" }}>
					{value}
					<Text type="secondary">
						{" "}
						(연차 {row.prior_annual_days} ·
						월차 {row.prior_monthly_days})
					</Text>
				</span>
			),
		},
		{
			title: "이번 기간 사용",
			dataIndex: "used_days",
			width: 170,
			align: "right",
			render: (value, row) => {
				const asOfYear = Number(
					String(asOf || "").slice(0, 4),
				);
				const showMonthlySplit =
					!Number.isNaN(asOfYear) &&
					asOfYear <= 2026;
				if (!showMonthlySplit) {
					return value;
				}
				return (
					<span style={{ whiteSpace: "nowrap" }}>
						{value}
						<Text type="secondary">
							{" "}
							(연차{" "}
							{row.used_annual_days ??
								0}{" "}
							· 월차{" "}
							{row.used_monthly_days ??
								0}
							)
						</Text>
					</span>
				);
			},
		},
		{
			title: "잔여",
			dataIndex: "remaining_days",
			width: 80,
			align: "right",
			render: (value) => <Text strong>{value}</Text>,
		},
		{
			title: "",
			width: 220,
			fixed: "right",
			render: (_, row) => (
				<Space>
					<Button
						size="small"
						onClick={() =>
							openCarryover(row)
						}>
						이월
					</Button>
					<Button
						size="small"
						onClick={() => openPrior(row)}>
						이전 사용 입력
					</Button>
				</Space>
			),
		},
	];

	const columns =
		sortMode === "job"
			? [
					{
						title: "소속",
						key: "job_group",
						width: 88,
						fixed: "left",
						onCell: (_, index) => ({
							rowSpan:
								groupSpans[
									index
								] || 0,
						}),
						render: (_, row) => (
							<span className="leave-balance__group-label">
								{getAttendanceJobGroup(
									row,
								)}
							</span>
						),
					},
					...baseColumns,
				]
			: baseColumns;

	return (
		<>
			<PageTitle title="잔여 연차 관리" />
			<div className="page">
				<PageHeader
					title="잔여 연차 관리"
				/>
				<LeavePromotionPanels
					asOf={asOf}
					sixMonths={resetSoonSix}
					twoMonths={resetSoonTwo}
					firstYearNine={firstYearNine}
					firstYearTwo={firstYearTwo}
					onChanged={loadBalances}
				/>
				<Card className="surface-card">
					<Space style={{ marginBottom: 16 }} wrap>
						<Input.Search
							allowClear
							placeholder="이름 검색"
							value={searchText}
							onChange={(event) =>
								setSearchText(
									event.target
										.value,
								)
							}
							style={{ width: 240 }}
						/>
						<Radio.Group
							value={sortMode}
							onChange={(e) =>
								setSortMode(
									e.target.value,
								)
							}
							optionType="button"
							options={[
								{
									label: "가나다 순",
									value: "name",
								},
								{
									label: "소속 순",
									value: "job",
								},
							]}
						/>
						<Text type="secondary">
							기준일 {asOf}
						</Text>
					</Space>
					<Table
						rowKey="user_id"
						loading={loading}
						columns={columns}
						dataSource={filtered}
						pagination={
							sortMode === "job"
								? false
								: {
										pageSize: 20,
										showSizeChanger: false,
									}
						}
						sticky={{ offsetHeader: 64 }}
						scroll={{
							x:
								sortMode === "job"
									? 1418
									: 1330,
						}}
					/>
				</Card>

				<Modal
					title={`${carryoverTarget?.name || ""} — 다음 기간 이월`}
					open={Boolean(carryoverTarget)}
					onCancel={() => setCarryoverTarget(null)}
					onOk={saveCarryover}
					confirmLoading={saving}
					destroyOnClose>
					<Form form={carryoverForm} layout="vertical">
						<Form.Item
							name="days"
							label="다음 기간으로 넘길 일수"
							rules={[
								{
									required: true,
									message: "일수를 입력하세요",
								},
							]}>
							<InputNumber
								min={0}
								style={{
									width: "100%",
								}}
							/>
						</Form.Item>
						<Form.Item name="note" label="메모">
							<TextArea rows={2} />
						</Form.Item>
					</Form>
				</Modal>

				<Modal
					title={`${priorTarget?.name || ""} — 이전 사용 입력`}
					open={Boolean(priorTarget)}
					onCancel={() => setPriorTarget(null)}
					onOk={savePrior}
					confirmLoading={saving}
					destroyOnClose>
					<Form form={priorForm} layout="vertical">
						<Form.Item
							name="prior_annual_days"
							label="이전 사용 연차"
							rules={[{ required: true }]}>
							<InputNumber
								min={0}
								step={0.5}
								precision={1}
								style={{
									width: "100%",
								}}
							/>
						</Form.Item>
						<Form.Item
							name="prior_monthly_days"
							label="이전 사용 월차"
							rules={[{ required: true }]}>
							<InputNumber
								min={0}
								step={0.5}
								precision={1}
								style={{
									width: "100%",
								}}
							/>
						</Form.Item>
						<Form.Item name="note" label="메모">
							<TextArea rows={2} />
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</>
	);
};

export default LeaveBalanceManage;
